import {type ReactNode, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav-events'
import {BannerStackActionsContext, type BannerStackActionsContextValue} from './banner-stack-actions-context'
import {
  InternalBannerStackDataContext,
  type BannerData,
  type BannerInput,
  type InitialBanner,
} from './internal-banner-stack-data-context'
import {
  InternalBannerRegistryContext,
  type InternalBannerRegistryContextValue,
} from './internal-banner-registry-context'

export interface BannerStackProviderProps {
  children: ReactNode
  /** Banners rendered on the provider's initial client or server render. IDs must be stable across hydration. */
  initialBanners?: InitialBanner[]
}

export function BannerStackProvider({children, initialBanners}: BannerStackProviderProps) {
  const [banners, setBanners] = useState<BannerData[]>(() => initialBanners ?? [])
  const navigationBannersRef = useRef<Map<string, BannerData> | null>(null)

  // Maps banner id -> its rendered root element (populated by `BannerStackOutlet` via
  // each `<Banner>`'s ref), so `focusBanner` can move focus into a specific banner.
  const bannerElsRef = useRef(new Map<string, HTMLElement>())

  const registerBannerEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      bannerElsRef.current.set(id, el)
    } else {
      bannerElsRef.current.delete(id)
    }
  }, [])

  const addBanner = useCallback((banner: BannerInput) => {
    const id = banner.id ?? crypto.randomUUID()
    const data: BannerData = {...banner, id}

    setBanners(prev => {
      const existingIndex = prev.findIndex(existing => existing.id === id)
      if (existingIndex === -1) return [...prev, data]
      return prev.map((existing, index) => (index === existingIndex ? data : existing))
    })

    return id
  }, [])

  const updateBanner = useCallback((id: string, banner: Partial<BannerInput>) => {
    setBanners(prev => prev.map(existing => (existing.id === id ? {...existing, ...banner} : existing)))
  }, [])

  const dismissBanner = useCallback((id: string) => {
    setBanners(prev => prev.filter(banner => banner.id !== id))
  }, [])

  const dismissAllBanners = useCallback(() => {
    setBanners([])
  }, [])

  const beginNavigation = useEffectEvent(() => {
    navigationBannersRef.current = new Map(
      banners.filter(banner => !banner.persistOnNavigation).map(banner => [banner.id, banner]),
    )
  })

  const finishNavigation = useCallback(() => {
    const navigationBanners = navigationBannersRef.current
    navigationBannersRef.current = null
    if (!navigationBanners) return

    setBanners(prev => prev.filter(banner => navigationBanners.get(banner.id) !== banner))
  }, [])

  const cancelNavigation = useCallback(() => {
    navigationBannersRef.current = null
  }, [])

  useEffect(() => {
    document.addEventListener(SOFT_NAV_STATE.START, beginNavigation)
    document.addEventListener(SOFT_NAV_STATE.END, finishNavigation)
    document.addEventListener(SOFT_NAV_STATE.ERROR, cancelNavigation)
    return () => {
      document.removeEventListener(SOFT_NAV_STATE.START, beginNavigation)
      document.removeEventListener(SOFT_NAV_STATE.END, finishNavigation)
      document.removeEventListener(SOFT_NAV_STATE.ERROR, cancelNavigation)
    }
  }, [cancelNavigation, finishNavigation])

  const focusBanner = useCallback((id: string) => {
    bannerElsRef.current.get(id)?.focus()
  }, [])

  const actionsValue = useMemo<BannerStackActionsContextValue>(
    () => ({addBanner, updateBanner, dismissBanner, dismissAllBanners, focusBanner}),
    [addBanner, updateBanner, dismissBanner, dismissAllBanners, focusBanner],
  )

  const dataValue = useMemo(() => ({banners}), [banners])

  const registryValue = useMemo<InternalBannerRegistryContextValue>(() => ({registerBannerEl}), [registerBannerEl])

  return (
    <BannerStackActionsContext value={actionsValue}>
      <InternalBannerRegistryContext value={registryValue}>
        <InternalBannerStackDataContext value={dataValue}>{children}</InternalBannerStackDataContext>
      </InternalBannerRegistryContext>
    </BannerStackActionsContext>
  )
}

BannerStackProvider.displayName = 'BannerStackProvider'
